import { Check, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { useTrackLibrary } from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { Modal } from "../../design-system/modal.js";
import { formatDuration } from "../../lib/format.js";
import type { Track } from "../../api/types.js";

/**
 * Choosing from the library, inside a playlist.
 *
 * The same paged, API-searched library the library screen reads — not a copy of it
 * and not a subset scoped to anything. Tracks already in this playlist are shown as
 * already in it rather than hidden, because a playlist that silently omits half the
 * library is harder to trust than one that says why a row cannot be chosen.
 */
export function AddTracksDialog({
  alreadyIn,
  onAdd,
  onClose,
  open,
}: {
  alreadyIn: ReadonlySet<string>;
  onAdd: (tracks: readonly Track[]) => void;
  onClose: () => void;
  open: boolean;
}) {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<Track[]>([]);
  const library = useTrackLibrary(open ? search : "", 30);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query);
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    if (!open) {
      setChosen([]);
      setQuery("");
    }
  }, [open]);

  const chosenIds = new Set(chosen.map((track) => track.id));

  return (
    <Modal onClose={onClose} open={open} title="Add tracks" width="40rem">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3"
          />
          <input
            aria-label="Search the library"
            autoFocus
            className="min-h-11 w-full rounded-sm border border-border bg-bg-2 pr-3 pl-9 text-sm text-ink transition-colors placeholder:text-ink-3 hover:border-border-hi focus:border-accent-line focus:outline-none"
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search title, artist or album"
            type="search"
            value={query}
          />
        </div>

        {library.loading ? (
          <StateSurface kind="loading" title="Reading the library" />
        ) : library.items.length === 0 ? (
          <StateSurface kind="empty" title="Nothing to add">
            {search.trim() === ""
              ? "The library has no tracks yet."
              : "No track in the library matches that."}
          </StateSurface>
        ) : (
          <ul className="max-h-80 overflow-y-auto rounded-sm border border-border">
            {library.items.map((track) => {
              const isIn = alreadyIn.has(track.id);
              const isChosen = chosenIds.has(track.id);
              return (
                <li
                  key={track.id}
                  className="border-b border-border last:border-b-0"
                >
                  <button
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
                      isIn
                        ? "cursor-not-allowed opacity-55"
                        : "hover:bg-surface-hi",
                    )}
                    disabled={isIn}
                    onClick={() => {
                      setChosen((current) =>
                        isChosen
                          ? current.filter((entry) => entry.id !== track.id)
                          : [...current, track],
                      );
                    }}
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "grid size-5 shrink-0 place-items-center rounded-sm border",
                        isChosen
                          ? "border-transparent bg-accent text-accent-ink"
                          : "border-border-hi",
                      )}
                    >
                      {isChosen ? <Check className="size-3.5" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">
                        {track.title}
                      </span>
                      <span className="block truncate text-xs text-ink-3">
                        {track.artist || "Unknown artist"}
                        {isIn ? " · already in this playlist" : ""}
                      </span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-ink-3 tabular-nums">
                      {formatDuration(track.durationMs)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {library.hasMore ? (
          <Button
            disabled={library.loadingMore}
            onClick={library.loadMore}
            size="small"
            variant="secondary"
          >
            {library.loadingMore ? "Reading…" : "Load more"}
          </Button>
        ) : null}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <span className="mr-auto text-xs text-ink-3">
            {chosen.length === 0
              ? "Nothing chosen"
              : `${String(chosen.length)} chosen`}
          </span>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={chosen.length === 0}
            onClick={() => {
              onAdd(chosen);
              onClose();
            }}
          >
            Add to playlist
          </Button>
        </div>
      </div>
    </Modal>
  );
}
